import { useCallback, useEffect, useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Switch } from '@antimatterhq/ui/components/switch';
import { Textarea } from '@antimatterhq/ui/components/textarea';
import { Text } from '@antimatterhq/ui/components/typography';
import { Form, FormikProvider, useFormikContext } from 'formik';
import { InfoTooltip } from '@antimatterhq/ui/components/infoTooltip';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import { PresetHeading } from '../../_partials/PresetHeading';
import { FormDataSourceNameValidatedInput } from './partials/FormDataSourceNameValidatedInput';
import { DataSourceNavigationButtons } from '@/components/button/dataSourceNavigation/DataSourceNavigationButtons';
import { TwoColumnLayout } from '@/components/container';
import { ConfirmationDialog } from '@/components/dialog/';
import { FormError } from '@/components/form/form';
import { FormDatabricksPathAndTableValidatedInput } from '@/components/form/input/FormDatabricksPathAndTableValidatedInput';
import { ExternalLink } from '@/components/link/external/ExternalLink';
import { ProcessingScheduleRadioGroup } from '@/components/radio';
import { AsyncValidationProvider } from '@/contexts/AsyncValidationContext';
import { useFormikWithAsyncValidation } from '@/hooks/form/useFormikWithAsyncValidation';
import {
  DatasourceSubmitType,
  useConfigDataSource,
} from '@/contexts/ConfigDataSourceContext';
import { useGetDatasourcePreset } from '@/hooks/content/useGetDatasourcePreset';
import { useCreateNotebook } from '@/hooks/dbui/useCreateNotebook';
import { useGetNotebookLink } from '@/hooks/dbui/useGetNotebookLink';
import {
  generateNotebookName,
  getGettingStartedInitialValues,
  mapGoldPresetsToDataSource,
  mapSilverPresetsToDataSource,
  setBronzePresets,
  setGettingStartedFormValues,
  setSkipPresets,
} from '@/lib/datasource';
import {
  isCompleted,
  setEnableOnComplete,
  shouldEnableOnComplete,
} from '@/lib/datasource/uiStatus';
import {
  dsDisabled,
  generateTemplateNotebook,
  helperNotebookTooltip,
} from '@/strings/StringsTooltips';
import {
  AddDataSourceSchema,
  AddDataSourceSchemaFormValues,
} from '@/zod/schemas/dataSource/AddDataSource';

export type GettingStartedFormProps = {
  submitAction: DatasourceSubmitType;
  setSubmitAction: (action: DatasourceSubmitType) => void;
  isLoadingPreset: boolean;
  submitDisabled?: boolean;
};

const GettingStartedForm = ({
  submitAction,
  setSubmitAction,
  isLoadingPreset,
  submitDisabled = false,
}: GettingStartedFormProps) => {
  const {
    handlePreviousStep,
    isSaving,
    isEdit,
    isReviewButtonVisible,
    setFormik,
    dataSource,
  } = useConfigDataSource();

  const { mutateAsync: mutateNotebook, isPending: isCreatingNotebook } =
    useCreateNotebook();

  const {
    values,
    handleChange,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    handleSubmit,
    dirty,
  } = useFormikContext<AddDataSourceSchemaFormValues>();

  const [notebookPath, setNotebookPath] = useState<string | undefined>(
    dataSource.spec.custom?.notebook || undefined,
  );

  const { data: { url: notebookLink } = {} } = useGetNotebookLink(notebookPath);

  const [showOverwriteNotebookDialog, setShowOverwriteNotebookDialog] =
    useState(false);

  useEffect(() => {
    setFormik({ handleSubmit, dirty });
  }, [handleSubmit, dirty, setFormik]);

  useEffect(() => {
    setNotebookPath(values.customNotebookPath || undefined);
  }, [values.customNotebookPath]);

  const createNotebook = useCallback(async () => {
    setShowOverwriteNotebookDialog(false);
    const path = generateNotebookName(values.name);
    await mutateNotebook(
      { path, templateType: 'datasource' },
      {
        onSuccess: async ({ data }) => {
          const notebook = data.notebookPath;
          setFieldValue('customNotebookPath', notebook);
          setNotebookPath(notebook);
          if (data.url) {
            window.open(data.url, '_blank', 'noopener,noreferrer');
          }
        },
        toasts: {
          success: 'Notebook created!',
          error: 'Failed to create notebook',
        },
      },
    );
  }, [mutateNotebook, values.name, setFieldValue]);

  const onCreateNotebook = useCallback(async () => {
    if (values.customNotebookPath) {
      setShowOverwriteNotebookDialog(true);
      return;
    }
    await createNotebook();
  }, [createNotebook, values.customNotebookPath]);

  return (
    <>
      <Form data-testid="getting-started-form">
        <Label htmlFor="name" required data-testid="name-label" id="name-label">
          Datasource name
        </Label>
        <FormDataSourceNameValidatedInput
          id="name"
          name="name"
          autoComplete="off"
          disabled={isSaving || isEdit}
          data-testid="name-input"
          aria-labelledby="name-label"
        />
        <FormError name="name" data-testid="name-error" />

        <Spacer size="base" data-testid="name-description-spacer" />

        <Label
          htmlFor="description"
          data-testid="description-label"
          id="description-label"
        >
          Description
        </Label>
        <Textarea
          id="description"
          name="description"
          value={values.description ?? undefined}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isSaving}
          className="max-w-form"
          data-testid="description-textarea"
          aria-labelledby="description-label"
        />
        <FormError name="description" data-testid="description-error" />

        <Spacer size="lg" />
        <div
          className="flex items-center gap-2"
          data-testid="helper-notebook-title"
        >
          <Text size="xl" data-testid="helper-notebook-text" bold>
            Helper notebook
          </Text>
          <InfoTooltip
            content={helperNotebookTooltip}
            data-testid="helper-notebook-tooltip"
          />
        </div>
        <Spacer size="base" data-testid="helper-notebook-spacer" />

        <div className="flex items-center gap-2">
          <FormDatabricksPathAndTableValidatedInput
            name="customNotebookPath"
            id="customNotebookPath"
            disabled={isSaving}
            constraint="mustExistReadWriteOrBeCreatable"
            validationType="path"
            placeholder="Enter notebook path"
          />
          <FormError
            name="customNotebookPath"
            data-testid="custom-notebook-path-error"
          />
          {!!notebookLink && (
            <ExternalLink
              url={notebookLink}
              data-testid="custom-notebook-path-external-link"
            />
          )}
        </div>
        <Spacer size="base" data-testid="custom-notebook-path-button-spacer" />

        <InfoTooltip
          content={generateTemplateNotebook}
          trigger={
            <Button
              variant="default"
              onClick={onCreateNotebook}
              loading={isCreatingNotebook}
              disabled={
                isSaving || isCreatingNotebook || !values.name || !!errors.name
              }
              leftIcon="lucide:notebook"
              data-testid="generate-template-notebook-button"
            >
              Generate a template notebook
            </Button>
          }
          data-testid="generate-template-tooltip"
        />

        <Spacer size="lg" />

        <Text
          size="xl"
          className="mb-2 mt-4 block"
          data-testid="enable-datasource-text"
          bold
        >
          Enable datasource
        </Text>
        <div
          className="flex items-center gap-2"
          data-testid="enable-datasource-container"
        >
          <div
            className="flex items-center gap-2"
            data-testid="enable-switch-container"
          >
            <Switch
              name="enabled"
              aria-labelledby="enabled-label"
              checked={values.enabled}
              onCheckedChange={(checked) => {
                handleChange({
                  target: {
                    name: 'enabled',
                    value: checked,
                  },
                });
              }}
              disabled={isSaving}
              data-testid="enabled-switch"
            />
            <Label
              htmlFor="enabled"
              id="enabled-label"
              className="m-0 block"
              data-testid="enabled-label"
              mb={0}
            >
              Enabled
            </Label>
          </div>
          <InfoTooltip
            content={dsDisabled}
            data-testid="enable-datasource-tooltip"
          />
        </div>

        <Spacer size="lg" />

        <Text
          size="xl"
          className="mb-2 mt-4 block"
          data-testid="processing-schedule-text"
          bold
        >
          Datasource processing schedule
        </Text>
        <ProcessingScheduleRadioGroup
          disabled={isSaving}
          handleChange={handleChange}
          values={values}
          errors={errors}
          touched={touched}
          handleBlur={handleBlur}
          data-testid="processing-schedule-radio-group"
        />

        <Spacer size="lg" />

        <DataSourceNavigationButtons
          disabled={isSaving || isLoadingPreset || submitDisabled}
          goToPreviousStep={handlePreviousStep}
          handlePreviousStep={() => setSubmitAction('previous')}
          handleNextStep={() => setSubmitAction('next')}
          handleReview={() => setSubmitAction('review')}
          loadingNext={submitAction === 'next' && isSaving}
          loadingReview={submitAction === 'review' && isSaving}
          isReviewButtonVisible={isReviewButtonVisible}
          data-testid="datasource-navigation-buttons"
        />
      </Form>
      <ConfirmationDialog
        open={showOverwriteNotebookDialog}
        title="Overwrite notebook path"
        body="This will overwrite the existing notebook path, are you sure?"
        onConfirm={() => createNotebook()}
        onCancel={() => setShowOverwriteNotebookDialog(false)}
        confirmButtonText="Overwrite"
        cancelButtonText="Cancel"
      />
    </>
  );
};

function DataSourceGettingStartedContent() {
  const {
    dataSource,
    handleNextStep,
    handleReview,
    save,
    isEdit,
    handlePreviousStep,
  } = useConfigDataSource();
  const { data: preset, isLoading: isLoadingPreset } = useGetDatasourcePreset({
    name: dataSource?.spec.usePreset,
    version: dataSource?.spec.usePresetVersion,
  });

  const [submitAction, setSubmitAction] = useState<DatasourceSubmitType>('');
  const enableOnComplete = shouldEnableOnComplete(dataSource);
  const reviewComplete = isCompleted(dataSource);

  const formik = useFormikWithAsyncValidation({
    initialValues: getGettingStartedInitialValues(dataSource, enableOnComplete),
    validate: toFormikValidate(AddDataSourceSchema),
    validationSchema: toFormikValidationSchema(AddDataSourceSchema),
    onSubmit: async (newValues) => {
      const overrideEnabled = !reviewComplete && newValues.enabled;
      let newDataSource = setGettingStartedFormValues(
        dataSource,
        newValues,
        overrideEnabled,
      );
      if (overrideEnabled) {
        newDataSource = setEnableOnComplete(newDataSource);
      }
      if (preset && !isEdit) {
        newDataSource = setBronzePresets(newDataSource, preset);
      }
      if (!isEdit) {
        newDataSource = setSkipPresets(newDataSource);
        newDataSource = mapSilverPresetsToDataSource(newDataSource, preset);
        newDataSource = mapGoldPresetsToDataSource(newDataSource, preset);
      }
      const result = await save(newDataSource);

      if (!result) {
        return;
      }

      // go to next step
      if (submitAction === 'next') {
        handleNextStep(newDataSource.metadata?.name || undefined);
      } else if (submitAction === 'review') {
        handleReview();
      } else if (submitAction === 'previous') {
        handlePreviousStep();
      }
    },
  });

  return (
    <TwoColumnLayout.Root data-testid="getting-started-container">
      <TwoColumnLayout.LeftColumn>
        <PresetHeading />
        <FormikProvider value={formik}>
          <GettingStartedForm
            submitAction={submitAction}
            setSubmitAction={setSubmitAction}
            isLoadingPreset={isLoadingPreset}
            submitDisabled={formik.submitDisabled}
          />
        </FormikProvider>
      </TwoColumnLayout.LeftColumn>
      <TwoColumnLayout.RightColumn> </TwoColumnLayout.RightColumn>
    </TwoColumnLayout.Root>
  );
}

export default function DataSourceGettingStarted() {
  return (
    <AsyncValidationProvider>
      <DataSourceGettingStartedContent />
    </AsyncValidationProvider>
  );
}
